import { observer } from 'mobx-lite';
import { useRef, useState, useEffect } from 'react';
import { MainStoreContextItem } from '../../Stores/MainStore';
import { BoundingBoxKalmanFilter, getFrontalityScore, type TrackedFace } from 'econnect-face-rec-web';
import { useVideoElementTracker } from './hooks/useVideoElementTracker';

const boxIdKalmanMap = new Map<number, BoundingBoxKalmanFilter>();

/** Stores the last computed frontality score per box-tracker ID so we can keep
 *  displaying it on frames where landmarks are unavailable. */
const boxIdFrontalityMap = new Map<number, number>();

function pruneDebugCaches(faces: readonly TrackedFace[] | null | undefined) {
    const activeBoxIds = new Set<number>();
    for (const face of faces ?? []) {
        if (face.boxTrackerId !== undefined) {
            activeBoxIds.add(face.boxTrackerId);
        }
    }

    for (const id of Array.from(boxIdKalmanMap.keys())) {
        if (!activeBoxIds.has(id)) boxIdKalmanMap.delete(id);
    }
    for (const id of Array.from(boxIdFrontalityMap.keys())) {
        if (!activeBoxIds.has(id)) boxIdFrontalityMap.delete(id);
    }
}

/** Duration (ms) to hold the spotlight at full opacity after losing face detection */
const SPOTLIGHT_HOLD_MS = 500;
/** Duration (ms) for the spotlight to fade out after the hold period */
const SPOTLIGHT_FADE_MS = 300;

export const FaceOverlayCanvas = observer(() => {
    const store = MainStoreContextItem.useStore();
    const { faceSpotlight } = store.settingsStore;
    const flipVideoHorizontally = store.settingsStore.effectiveFlipHorizontally;
    const { faces, laplacianByBoxId } = store.faceServiceStore;
    const { showNerdStatistics } = store.settingsStore;
    const videoRef = store.mediaDeviceStore.videoElementRef;
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Use the video element tracker hook
    const { updateKey } = useVideoElementTracker(videoRef);

    // State to trigger re-draw when video dimensions change via ResizeObserver
    const [videoDisplayDimensions, setVideoDisplayDimensions] = useState({
        width: 0,
        height: 0,
    });

    // Spotlight hold/fade state — prevents flicker when face detection drops frames
    const lastSpotlightRef = useRef<{ spotX: number; spotY: number; spotRadius: number } | null>(null);
    const faceGoneAtRef = useRef<number | null>(null);
    const [spotlightRedrawTick, setSpotlightRedrawTick] = useState(0);

    // Clear Kalman filters and frontality cache when component unmounts to prevent memory leaks
    useEffect(() => {
        return () => {
            boxIdKalmanMap.clear();
            boxIdFrontalityMap.clear();
            lastSpotlightRef.current = null;
            faceGoneAtRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (showNerdStatistics) {
            pruneDebugCaches(faces);
        } else {
            boxIdKalmanMap.clear();
            boxIdFrontalityMap.clear();
        }
    }, [faces, showNerdStatistics]);

    useEffect(() => {
        const videoElement = videoRef.current;
        const canvasElement = canvasRef.current;

        if (!videoElement || !canvasElement) return;

        // Use ResizeObserver to detect changes in video element's display size
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (canvasElement.width !== width || canvasElement.height !== height) {
                    canvasElement.width = width;
                    canvasElement.height = height;
                    // Trigger a re-render/re-draw by updating state
                    setVideoDisplayDimensions({ width, height });
                }
            }
        });

        observer.observe(videoElement);

        // Initial sizing
        const initialWidth = videoElement.clientWidth;
        const initialHeight = videoElement.clientHeight;
        if (canvasElement.width !== initialWidth) {
            canvasElement.width = initialWidth;
        }
        if (canvasElement.height !== initialHeight) {
            canvasElement.height = initialHeight;
        }
        setVideoDisplayDimensions({
            width: initialWidth,
            height: initialHeight,
        });

        return () => {
            observer.unobserve(videoElement);
            observer.disconnect();
        };
    }, [videoRef, canvasRef, updateKey]); // Effect runs when refs are available or video element changes

    useEffect(() => {
        // Drawing logic, depends on faces and the actual display dimensions of the video
        const videoElement = videoRef.current;
        const canvasElement = canvasRef.current;

        if (
            !videoElement ||
            !canvasElement ||
            videoElement.videoWidth === 0 || // Intrinsic video track not loaded
            videoElement.videoHeight === 0 ||
            canvasElement.width === 0 || // Canvas not yet sized by ResizeObserver
            canvasElement.height === 0
        ) {
            if (canvasElement) {
                // Clear if canvas exists but video not ready
                const ctx = canvasElement.getContext('2d', {
                    desynchronized: true,
                    alpha: true, // Ensure alpha channel is enabled
                });

                if (ctx) {
                    ctx.fillStyle = 'rgba(0,0,0,0)';
                    ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
                }
            }
            return;
        }

        const ctx = canvasElement.getContext('2d', {
            desynchronized: true,
            alpha: true, // Ensure alpha channel is enabled
        });
        if (!ctx) return;

        const displayWidth = canvasElement.width; // Actual width of canvas/video element on screen
        const displayHeight = canvasElement.height; // Actual height of canvas/video element on screen
        const intrinsicWidth = videoElement.videoWidth; // Native width of the video stream
        const intrinsicHeight = videoElement.videoHeight; // Native height of the video stream
        ctx.fillStyle = 'rgba(0,0,0,0)';
        ctx.clearRect(0, 0, displayWidth, displayHeight);

        // Calculate scaling and offset for 'object-fit: cover'
        // (moved before faces check so spotlight hold/fade can use it)
        let drawX = 0,
            drawY = 0,
            drawWidth = 0,
            drawHeight = 0;

        const intrinsicAspect = intrinsicWidth / intrinsicHeight;
        const displayAspect = displayWidth / displayHeight;

        if (intrinsicAspect > displayAspect) {
            drawHeight = displayHeight;
            drawWidth = displayHeight * intrinsicAspect;
            drawX = (displayWidth - drawWidth) / 2;
            drawY = 0;
        } else {
            drawWidth = displayWidth;
            drawHeight = displayWidth / intrinsicAspect;
            drawX = 0;
            drawY = (displayHeight - drawHeight) / 2;
        }

        // --- Spotlight overlay with hold/fade to prevent flicker on dropped detection frames ---
        if (faceSpotlight) {
            let spotlightOpacity = 0;
            let effectiveSpotlight: { spotX: number; spotY: number; spotRadius: number } | null = null;

            if (faces && faces.length > 0) {
                // Face detected — compute spotlight from biggest face and store for hold/fade
                let biggestFace = faces[0];
                for (const f of faces) {
                    if (f.boundingBox.width * f.boundingBox.height > biggestFace.boundingBox.width * biggestFace.boundingBox.height) {
                        biggestFace = f;
                    }
                }
                const bb = biggestFace.boundingBox;
                let spotX = drawX + ((bb.origin.x + bb.width / 2) / intrinsicWidth) * drawWidth;
                const spotY = drawY + ((bb.origin.y + bb.height / 2) / intrinsicHeight) * drawHeight;
                const spotW = (bb.width / intrinsicWidth) * drawWidth;
                const spotH = (bb.height / intrinsicHeight) * drawHeight;
                if (flipVideoHorizontally) spotX = displayWidth - spotX;
                const spotRadius = Math.max(spotW, spotH);

                lastSpotlightRef.current = { spotX, spotY, spotRadius };
                faceGoneAtRef.current = null;
                effectiveSpotlight = { spotX, spotY, spotRadius };
                spotlightOpacity = 1;
            } else if (lastSpotlightRef.current) {
                // No face detected — hold the last spotlight position, then fade out
                if (faceGoneAtRef.current === null) {
                    faceGoneAtRef.current = performance.now();
                }
                const elapsed = performance.now() - faceGoneAtRef.current;
                if (elapsed < SPOTLIGHT_HOLD_MS) {
                    spotlightOpacity = 1;
                    effectiveSpotlight = lastSpotlightRef.current;
                } else if (elapsed < SPOTLIGHT_HOLD_MS + SPOTLIGHT_FADE_MS) {
                    spotlightOpacity = 1 - (elapsed - SPOTLIGHT_HOLD_MS) / SPOTLIGHT_FADE_MS;
                    effectiveSpotlight = lastSpotlightRef.current;
                } else {
                    // Fade complete — clear stored spotlight
                    lastSpotlightRef.current = null;
                    faceGoneAtRef.current = null;
                }
            }

            if (effectiveSpotlight && spotlightOpacity > 0) {
                const { spotX, spotY, spotRadius } = effectiveSpotlight;
                ctx.save();
                ctx.fillStyle = `rgba(0, 0, 0, ${0.7 * spotlightOpacity})`;
                ctx.fillRect(0, 0, displayWidth, displayHeight);
                ctx.globalCompositeOperation = 'destination-out';
                const gradient = ctx.createRadialGradient(spotX, spotY, 0, spotX, spotY, spotRadius);
                gradient.addColorStop(0, `rgba(0, 0, 0, ${spotlightOpacity})`);
                gradient.addColorStop(0.5, `rgba(0, 0, 0, ${0.8 * spotlightOpacity})`);
                gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
                ctx.fillStyle = gradient;
                ctx.beginPath();
                ctx.arc(spotX, spotY, spotRadius, 0, Math.PI * 2);
                ctx.fill();
                ctx.restore();
            }
        }

        if (!faces || faces.length === 0) {
            return;
        }

        if (showNerdStatistics) {
            faces.forEach((face) => {
                const { landmarks } = face;

                let boundingBox = face.boundingBox;
                if (face.boxTrackerId !== undefined && !boxIdKalmanMap.has(face.boxTrackerId)) {
                    boxIdKalmanMap.set(face.boxTrackerId, new BoundingBoxKalmanFilter());
                }

                boundingBox = boxIdKalmanMap.get(face.boxTrackerId as number)?.filter(boundingBox) ?? boundingBox;

                // Transform boundingBox coordinates from intrinsic video space to canvas space
                let bbX = drawX + (boundingBox.origin.x / intrinsicWidth) * drawWidth;
                const bbY = drawY + (boundingBox.origin.y / intrinsicHeight) * drawHeight;
                const bbWidth = (boundingBox.width / intrinsicWidth) * drawWidth;
                const bbHeight = (boundingBox.height / intrinsicHeight) * drawHeight;

                // Mirror x-coordinate when video is flipped horizontally
                if (flipVideoHorizontally) {
                    bbX = displayWidth - bbX - bbWidth;
                }

                // Define a radius for the rounded corners, make it responsive.
                // This can be a single number for all corners, or an array for individual corners.
                const cornerRadius = Math.max(5, Math.min(20, displayWidth / 50));

                ctx.strokeStyle = 'lime';
                // Make lineWidth responsive to the displayed video size
                ctx.lineWidth = Math.max(1, Math.min(4, displayWidth / 250));
                ctx.beginPath();
                ctx.roundRect(bbX, bbY, bbWidth, bbHeight, cornerRadius);
                ctx.stroke();

                // Display quality, acceptability, and frontality
                if (face.quality) {
                    const qualityScore = face.quality ? face.quality.toFixed(4) : 'N/A';

                    // Compute frontality from landmarks when available, otherwise use last-known value
                    let frontalityDisplay = 'N/A';
                    if (landmarks) {
                        const frontality = getFrontalityScore(landmarks);
                        frontalityDisplay = frontality.toFixed(4);
                        if (face.boxTrackerId !== undefined) {
                            boxIdFrontalityMap.set(face.boxTrackerId, frontality);
                        }
                    } else if (face.boxTrackerId !== undefined && boxIdFrontalityMap.has(face.boxTrackerId)) {
                        frontalityDisplay = boxIdFrontalityMap.get(face.boxTrackerId)!.toFixed(4);
                    }

                    const sid = face.faceSubjectId ? face.faceSubjectId.substring(0, 8) : 'N/A';
                    const bid = face.boxTrackerId ?? 'N/A';
                    const acceptability = face.acceptability?.toFixed(4) ?? 'N/A';
                    const laplacian = face.boxTrackerId !== undefined
                        ? laplacianByBoxId.get(face.boxTrackerId)
                        : undefined;
                    const laplacianDisplay = laplacian !== undefined ? laplacian.toFixed(1) : 'N/A';

                    const lines = [
                        `SID ${sid}  BID ${bid}`,
                        `Q ${qualityScore}  A ${acceptability}`,
                        `F ${frontalityDisplay}  L ${laplacianDisplay}`,
                    ];

                    const fontSize = Math.max(7, Math.min(13, displayWidth / 60));
                    const lineHeight = fontSize * 1.4;
                    ctx.font = `500 ${fontSize}px 'Segoe UI', system-ui, sans-serif`;

                    // Measure max line width for background
                    const padding = fontSize * 0.5;
                    let maxLineWidth = 0;
                    for (const line of lines) {
                        const w = ctx.measureText(line).width;
                        if (w > maxLineWidth) maxLineWidth = w;
                    }

                    const bgWidth = maxLineWidth + padding * 2;
                    const bgHeight = lines.length * lineHeight + padding * 1.5;
                    const bgX = bbX;
                    const bgY = bbY + bbHeight + 4;
                    const bgRadius = Math.max(3, fontSize * 0.35);

                    // Semi-transparent background pill
                    ctx.save();
                    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
                    ctx.beginPath();
                    ctx.roundRect(bgX, bgY, bgWidth, bgHeight, bgRadius);
                    ctx.fill();

                    // Draw text lines
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
                    ctx.textBaseline = 'top';
                    lines.forEach((line, i) => {
                        ctx.fillText(line, bgX + padding, bgY + padding * 0.75 + i * lineHeight);
                    });
                    ctx.restore();
                }

                if (landmarks) {
                    ctx.fillStyle = 'red';
                    // Make landmarkSize responsive
                    const landmarkSize = Math.max(1, Math.min(5, displayWidth / 300));
                    [landmarks.leftEye, landmarks.rightEye, landmarks.nose, landmarks.leftMouth, landmarks.rightMouth].forEach((point) => {
                        if (point) {
                            // Transform landmark point coordinates
                            let pX = drawX + (point.x / intrinsicWidth) * drawWidth;
                            const pY = drawY + (point.y / intrinsicHeight) * drawHeight;
                            if (flipVideoHorizontally) {
                                pX = displayWidth - pX;
                            }
                            ctx.beginPath();
                            ctx.arc(pX, pY, landmarkSize, 0, 2 * Math.PI);
                            ctx.fill();
                        }
                    });
                }
            });
        }
    }, [faces, videoRef, canvasRef, videoDisplayDimensions, updateKey, flipVideoHorizontally, faceSpotlight, showNerdStatistics, spotlightRedrawTick, laplacianByBoxId]); // Re-draw when faces, video element, or video display dimensions change

    // RAF loop to trigger redraws during spotlight hold/fade when no faces are detected
    useEffect(() => {
        if (faceSpotlight && (!faces || faces.length === 0) && lastSpotlightRef.current) {
            let rafId: number;
            const tick = () => {
                if (lastSpotlightRef.current) {
                    setSpotlightRedrawTick((c) => c + 1);
                    rafId = requestAnimationFrame(tick);
                }
            };
            rafId = requestAnimationFrame(tick);
            return () => cancelAnimationFrame(rafId);
        }
    }, [faces, faceSpotlight]);

    // This specific clearing logic might be redundant due to the main drawing effect's clearing
    // but kept if there's a desire to clear explicitly when video metadata is not yet available.
    useEffect(() => {
        const videoElement = videoRef.current;
        const canvasElement = canvasRef.current;
        if (
            canvasElement &&
            videoElement &&
            (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) &&
            canvasElement.width > 0 &&
            canvasElement.height > 0
        ) {
            const ctx = canvasElement.getContext('2d', {
                desynchronized: true,
                alpha: true, // Ensure alpha channel is enabled
            });

            if (ctx) {
                ctx.fillStyle = 'rgba(0,0,0,0)';
                ctx.clearRect(0, 0, canvasElement.width, canvasElement.height);
            }
        }
    }, [videoRef, canvasRef, videoDisplayDimensions, updateKey]);

    if (!showNerdStatistics && !faceSpotlight) {
        return null;
    }

    return (
        <canvas
            ref={canvasRef}
            style={{
                position: 'absolute',
                backgroundColor: 'transparent', // Override default StyledVideo bg
                top: 0,
                left: 0,
                opacity: 0.998,
                width: '100%', // CSS style to fill parent
                height: '100%', // CSS style to fill parent
                pointerEvents: 'none',

            }}
        />
    );
});

FaceOverlayCanvas.displayName = 'FaceOverlayCanvas';
